// src/component/VideoAdminPage.js
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../api/firebase";
import { useNavigate } from "react-router-dom";
import { AppThemeContext } from "./AppShell";

// 期の候補
const PAYMENT_TERMS = [1, 2, 3, 4];

// YouTube ID 抽出
const extractYouTubeId = (url) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|[^/\n\s]+\/\S*?[^\w\s-]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const buildYouTubePlayerUrl = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

// YouTube プレビュー用 URL（コントロール非表示）
const buildYouTubePreviewUrl = (url) => {
  const id = extractYouTubeId(url);
  if (!id) return null;
  return `https://www.youtube.com/embed/${id}?controls=0&modestbranding=1&rel=0&playsinline=1`;
};

// Vimeo ID / ハッシュ抽出
const extractVimeoId = (url) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/\S*)?/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const extractVimeoHash = (url) => {
  const regex = /vimeo\.com\/\d+\/([a-zA-Z0-9]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// プレビュー iframe を返すヘルパー
const renderPreviewIframe = (url, title) => {
  if (!url) return null;

  // YouTube
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    const src = buildYouTubePreviewUrl(url);
    if (!src) return null;
    return (
      <iframe
        src={src}
        title={title || "YouTube video"}
        frameBorder="0"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen={false}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: 0,
          pointerEvents: "none", // 再生させない
        }}
      />
    );
  }

  // Vimeo
  if (url.includes("vimeo.com")) {
    const id = extractVimeoId(url);
    const h = extractVimeoHash(url);
    if (!id) return null;
    const src = `https://player.vimeo.com/video/${id}${
      h ? `?h=${h}` : ""
    }&title=0&byline=0&portrait=0`;
    return (
      <iframe
        src={src}
        title={title || "Vimeo video"}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen={false}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: 0,
          pointerEvents: "none", // 再生させない
        }}
      />
    );
  }

  // その他の埋め込み URL
  return (
    <iframe
      src={url}
      title={title || "video preview"}
      frameBorder="0"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
        pointerEvents: "none",
      }}
    />
  );
};

// 説明文のトリム用
const truncate = (text, max = 200) =>
  text && text.length > max ? text.slice(0, max) + "…" : text || "";

// 日時整形
const formatDateTime = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleString("ja-JP");
};

export default function VideoAdminPage() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};

  // 🎨 AppShell から色をもらう
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const [videos, setVideos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const [previewVideo, setPreviewVideo] = useState(null);

  const handleOpenPreview = (video) => {
    setPreviewVideo(video);
  };

  const handleClosePreview = () => {
    setPreviewVideo(null);
  };

  // Firestore から動画を購読
  useEffect(() => {
    const q = query(collection(db, "videos"), orderBy("order", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVideos(list);
    });
    return () => unsub();
  }, []);

  const handleOpenDialog = (video = null) => {
    if (video) {
      setCurrentVideo(video);
      setTitle(video.title || "");
      setUrl(video.url || "");
      setDescription(video.description || "");
    } else {
      setCurrentVideo(null);
      setTitle("");
      setUrl("");
      setDescription("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentVideo(null);
    setTitle("");
    setUrl("");
    setDescription("");
  };

  const isFormValid = () =>
    title.trim() !== "" && url.trim() !== "" && description.trim() !== "";

  const handleSaveVideo = async () => {
    try {
      if (currentVideo) {
        // 既存動画の更新
        await updateDoc(doc(db, "videos", currentVideo.id), {
          title,
          url,
          description,
        });
      } else {
        // 新規動画の作成（先頭に来るように order を決める）
        const firstOrder = videos.length ? videos[0].order + 1 : 1;
        await addDoc(collection(db, "videos"), {
          title,
          url,
          description,
          order: firstOrder,
          createdAt: serverTimestamp(),
          allowedTerm: [],
        });
      }
    } catch (e) {
      console.error("Failed to save video:", e);
    } finally {
      handleCloseDialog();
    }
  };

  const handleOpenDeleteDialog = (video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setVideoToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteVideo = async () => {
    if (!videoToDelete) return;
    try {
      await deleteDoc(doc(db, "videos", videoToDelete.id));
    } catch (e) {
      console.error("Failed to delete video:", e);
    } finally {
      setVideoToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // 期チェックのトグル
  const handleToggleTerm = async (video, term) => {
    const termStr = String(term);
    const current = (video.allowedTerm || []).map(String);
    const has = current.includes(termStr);
    const next = has
      ? current.filter((t) => t !== termStr)
      : [...current, termStr];

    try {
      await updateDoc(doc(db, "videos", video.id), {
        allowedTerm: next,
      });
      setVideos((prev) =>
        prev.map((v) => (v.id === video.id ? { ...v, allowedTerm: next } : v))
      );
    } catch (e) {
      console.error("Failed to update allowedTerm:", e);
    }
  };

  // 並び替え（上へ）
  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const upper = videos[index - 1];
    const current = videos[index];

    try {
      await runTransaction(db, async (tx) => {
        const upperRef = doc(db, "videos", upper.id);
        const currentRef = doc(db, "videos", current.id);
        tx.update(upperRef, { order: current.order });
        tx.update(currentRef, { order: upper.order });
      });

      setVideos((prev) => {
        const arr = [...prev];
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
        return arr;
      });
    } catch (e) {
      console.error("Failed to move up:", e);
    }
  };

  // 並び替え（下へ）
  const handleMoveDown = async (index) => {
    if (index === videos.length - 1) return;
    const lower = videos[index + 1];
    const current = videos[index];

    try {
      await runTransaction(db, async (tx) => {
        const lowerRef = doc(db, "videos", lower.id);
        const currentRef = doc(db, "videos", current.id);
        tx.update(lowerRef, { order: current.order });
        tx.update(currentRef, { order: lower.order });
      });

      setVideos((prev) => {
        const arr = [...prev];
        [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
        return arr;
      });
    } catch (e) {
      console.error("Failed to move down:", e);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        pb: { xs: 3, sm: 4 },
      }}
    >
      {/* ヘッダー行 */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN / VIDEOS
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.03em",
              color: textBase,
            }}
          >
            動画管理
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}
          >
            動画の登録・編集・並び替えと、各期への公開範囲の設定を行います。
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/admin")}
          >
            管理者メニューに戻る
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            動画を追加
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* 動画カード一覧：4 列グリッド（md 以上） */}
      <Grid container spacing={2.5}>
        {videos.map((video, index) => {
          const created = video.createdAt && formatDateTime(video.createdAt);
          const allowedSet = new Set(
            (video.allowedTerm || []).map((t) => String(t))
          );

          return (
            <Grid item key={video.id} xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ p: 2.2, pb: 1.5 }}>
                  <Stack spacing={1.5}>
                    {/* 表示順 + 作成日 */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: textBase,
                        }}
                      >
                        No.{index + 1}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        作成日: {created || "未設定"}
                      </Typography>
                    </Box>

                    {/* タイトル */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: textBase,
                        wordBreak: "break-word",
                      }}
                    >
                      {video.title || "（タイトル未設定）"}
                    </Typography>

                    {/* サムネイル（クリックでプレビュー） */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                        "&::before": {
                          content: '""',
                          display: "block",
                          paddingTop: "56.25%", // 16:9
                        },
                        cursor: "pointer",
                        background:
                          "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                          `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                      }}
                      onClick={() => handleOpenPreview(video)}
                    >
                      {renderPreviewIframe(video.url || "", video.title)}
                    </Box>

                    {/* 説明（2行） */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        color: "rgba(0,0,0,0.75)",
                        lineHeight: 1.6,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                      }}
                    >
                      {truncate(
                        video.description || "説明文がまだ登録されていません。",
                        200
                      )}
                    </Typography>

                    {/* 期のチェックボックス */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      {PAYMENT_TERMS.map((term) => (
                        <FormControlLabel
                          key={term}
                          control={
                            <Checkbox
                              size="small"
                              checked={allowedSet.has(String(term))}
                              onChange={() => handleToggleTerm(video, term)}
                            />
                          }
                          label={`${term}期`}
                          sx={{
                            m: 0,
                            mr: 1.5,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 12,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Stack>
                </CardContent>

                {/* 下部アクション */}
                <CardActions
                  sx={{
                    px: 2,
                    pb: 1.5,
                    pt: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: "auto",
                  }}
                >
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === videos.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(video)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleOpenDeleteDialog(video)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* 追加 / 編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>動画を{currentVideo ? "編集" : "追加"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="動画のタイトル"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="動画のURL (YouTube / Vimeo / その他埋め込み URL)"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <TextField
            margin="dense"
            label="動画の説明"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "rgba(0,0,0,0.6)" }}
          >
            ※ 期ごとの公開範囲は保存後にカード下部のチェックボックスから設定できます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSaveVideo}
            variant="contained"
            disabled={!isFormValid()}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>動画の削除</DialogTitle>
        <DialogContent>
          <Typography>
            本当にこの動画を削除しますか？
            <br />
            タイトル：「{videoToDelete?.title || "（タイトル未設定）"}」
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button color="error" onClick={handleDeleteVideo}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* ▼ 動画再生プレビュー ダイアログ */}
      <Dialog
        open={Boolean(previewVideo)}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: surface,
          },
        }}
      >
        {previewVideo && (
          <>
            <DialogTitle
              sx={{
                pr: 6,
                fontWeight: 700,
                color: textBase,
              }}
            >
              {previewVideo.title || "動画プレビュー"}
              <IconButton
                onClick={handleClosePreview}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 2 }}>
              {/* 16:9 のプレイヤー枠 */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
                  mb: 2,
                  "&::before": {
                    content: '""',
                    display: "block",
                    paddingTop: "56.25%", // 16:9
                  },
                }}
              >
                {(() => {
                  const url = previewVideo.url || "";

                  // Vimeo
                  if (url.includes("vimeo.com")) {
                    const id = extractVimeoId(url);
                    const h = extractVimeoHash(url);
                    if (!id) return null;
                    const src = `https://player.vimeo.com/video/${id}${
                      h ? `?h=${h}` : ""
                    }`;
                    return (
                      <iframe
                        src={src}
                        title={previewVideo.title || "Vimeo video"}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                      />
                    );
                  }

                  // YouTube
                  if (url.includes("youtube.com") || url.includes("youtu.be")) {
                    const src = buildYouTubePlayerUrl(url);
                    if (!src) return null;
                    return (
                      <iframe
                        src={src}
                        title={previewVideo.title || "YouTube video"}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                      />
                    );
                  }

                  // その他 iframe
                  return (
                    <iframe
                      src={url}
                      title={previewVideo.title || "video"}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                    />
                  );
                })()}
              </Box>

              {previewVideo.description && (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color: textBase,
                  }}
                >
                  {previewVideo.description}
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
